import React, { useState } from "react";
import FaqItem from "../FaqItem/FaqItem";
import SectionHeading from "../SectionHeading/SectionHeading";

const FAQS = [
  {
    question: "How is FieldCoach priced?",
    answer: [
      "FieldCoach is <b>$175 per license per month</b> for all CertainPath members on a 1-year contract. <b>CertainPath Training members receive a discounted rate of $150 per license per month</b> — a $25/month savings per seat. Pricing is per license, so you only pay for the reps actively using FieldCoach. Volume discounts are available for larger teams.",
    ],
  },
  {
    question: "What kind of commitment is required?",
    answer: [
      "FieldCoach is offered on a <b>12-month contract with monthly billing</b>. Licenses are per seat, so you can scale up at any time as your team grows. Pricing remains predictable — no tier juggling, no surprise overages.",
    ],
  },
  {
    question: "How quickly can my team get started?",
    answer: [
      "Most teams are operational <b>within a week</b>. Reps download the FieldCoach mobile app, complete a quick Voice ID setup, and can begin recording their next appointment. Managers get immediate access to the analytics portal and at-a-glance dashboards.",
      "Your CertainPath onboarding team handles organization setup, user provisioning, and script configuration so the system is ready to use out of the box.",
    ],
  },
  {
    question: "Will my reps actually use it?",
    answer: [
      "FieldCoach is built into your rep's <b>existing CRM workflow</b>. Recording starts with a single tap from the appointment screen — no extra workflow, no second device required. The mobile app is available on iOS and Android.",
      "Because recordings auto-link to the CRM appointment, there's no extra data entry. The system stays out of the way so reps can focus on the customer.",
    ],
  },
  {
    question: "How does Voice ID work?",
    answer: [
      "Voice ID is a <b>one-time setup</b> where your rep records a short voice sample. From then on, FieldCoach automatically separates your rep's voice from the customer's across every recording — eliminating “who said what” disputes and sharpening coaching accuracy.",
      "For best results, we recommend recording on a separate device from the CRM tablet when possible.",
    ],
  },
  {
    question: "What's included with every license?",
    answer: [
      "Every FieldCoach license includes: <b>unlimited call recordings</b>, AI-powered transcription, Voice ID speaker separation, script & process adherence scoring, sentiment and tone analysis, objection-handling evaluation, AI coaching insights with timestamps, at-a-glance team dashboards, and dedicated CertainPath support.",
    ],
  },
  {
    question: "How is FieldCoach different from generic call recording?",
    answer: [
      "<b>FieldCoach is the only AI coaching platform built around the CertainPath Operating System.</b> Generic call recorders capture audio. FieldCoach captures, transcribes, scores, and analyzes — and every score, every insight, every benchmark is calibrated to your CertainPath scripts, processes, and standards.",
      "It's purpose-built for home service contractors by people who understand the trades. From script adherence to discounting patterns, every analytic reflects how CertainPath members actually sell in the home. No other coaching tool — generic or otherwise — can make that claim.",
    ],
  },
  {
    question: "What about call consent and compliance?",
    answer: [
      "FieldCoach is designed to work within standard call-recording practices. Reps inform customers at the start of the appointment that the conversation is being recorded for training and quality purposes — consistent with industry standard practice across home services.",
      "Recording disclosure can also be included in your standard service-agreement language. Speak with your CertainPath success team for guidance specific to your state and operating areas.",
    ],
  },
];

const FaqBlock: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section
      className="relative bg-mist pb-12 pt-24 dark:bg-slate-950"
      id="faq"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto mb-14 max-w-3xl text-center">
          <SectionHeading>Frequently Asked Questions</SectionHeading>
          <p className="mt-4 text-lg text-gray-500 dark:text-gray-400">
            Everything you need to know about FieldCoach.
          </p>
        </div>

        <div className="mx-auto flex max-w-4xl flex-col gap-3.5">
          {FAQS.map((faq, index) => (
            <FaqItem
              answer={faq.answer}
              isOpen={openIndex === index}
              key={faq.question}
              onToggle={() =>
                setOpenIndex((current) => (current === index ? null : index))
              }
              question={faq.question}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FaqBlock;
